import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { MdVerifiedUser } from "react-icons/md";
import api from "../../configs/api";
import { useAuth } from "../../context/AuthContext";
import { Z_INDEX } from "../../utils/constants";
import {
  formatDate,
  formatPrettyAddress,
  formatPrice,
} from "../../utils/helpers";
import { showError, showSuccess } from "../../utils/toastUtils";

const ContactOwnerModal = ({ isOpen, onClose, propertyData }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    leadName: "",
    phoneNumber: "",
    email: "",
    location: "",
    searchCriteria: {
      searchFor: "",
      propertyLocation: "",
      budget: {
        min: "",
        max: "",
      },
    },
    matchedPropertyId: "",
    assignedAgent: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      leadName: user?.fullName || "",
      phoneNumber: user?.phone || "",
      email: user?.email || "",
      location: user?.preferredCity || "",

      // UPDATED HERE
      searchCriteria: {
        searchFor: propertyData?.title || "",
        propertyLocation: formatPrettyAddress(propertyData?.location) || "", // ← UPDATED
        budget: {
          min: "",
          max: "",
        },
      },

      matchedPropertyId: propertyData?._id || "",
      assignedAgent: propertyData?.listedBy?._id || "",
    });
  }, [isOpen, user, propertyData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const updated = { ...prev };
        let current = updated;

        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;
        return updated;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    const submitData = {
      leadName: formData.leadName,
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      location: formData.location,
      searchCriteria: {
        searchFor: formData.searchCriteria.searchFor,
        propertyLocation: formData.searchCriteria.propertyLocation,
        budget: {
          min: formData.searchCriteria.budget.min
            ? Number(formData.searchCriteria.budget.min)
            : 0,
          max: formData.searchCriteria.budget.max
            ? Number(formData.searchCriteria.budget.max)
            : 0,
        },
      },
      matchedPropertyId: formData.matchedPropertyId,
      assignedAgent: formData.assignedAgent,
    };

    try {
      const response = await api.post("/leads/", submitData);

      if (response.data.success) {
        setSubmitted(true);
        setSuccess(true);
        showSuccess("Request submitted! You’ll be contacted soon.");
        setTimeout(() => {
          handleClose();
          setSubmitted(false);
        }, 1500);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to submit lead. Please try again."
      );
      showError("Failed to submit lead. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError("");
    setSuccess(false);
    onClose();
    setSubmitted(false);
  };

  if (!isOpen) return null;

  const getPropertyPrice = () => {
    if (!propertyData?.property) return null;

    if (
      propertyData.type === "residential" ||
      propertyData.type === "project"
    ) {
      return (
        propertyData.property.floorPlan?.expectedPrice ||
        propertyData.property.price
      );
    }
    if (propertyData.type === "commercial") {
      return propertyData.property.allowedForConstructionDetails?.expectedPrice;
    }
    if (propertyData.type === "land") {
      return propertyData.property.expectedPrice;
    }
    return null;
  };

  return createPortal(
    <div
      style={{ zIndex: Z_INDEX.modal }}
      className="fixed inset-0  flex items-center justify-center p-3 overflow-y-auto"
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      ></div>

      <div className="relative z-10 w-full max-w-3xl my-auto">
        {propertyData && (
          <div className="bg-white flex flex-col gap-y-1 w-full  rounded-t-lg px-5 py-3 mb-0.5">
            <div className="flex items-center justify-between w-full">
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                You are requesting to view advertiser details
              </h3>
              <button
                onClick={handleClose}
                className="text-text-secondary hover:text-btn-brandRed transition-colors ml-4"
                disabled={loading}
              >
                <IoClose size={22} />
              </button>
            </div>
            <div className="flex-1 items-center text-xs text-text-secondary">
              <div className="flex items-center gap-3 ">
                {getPropertyPrice() && (
                  <span className="font-semibold text-btn-brandRed">
                    {formatPrice(getPropertyPrice())}
                  </span>
                )}
                <span>
                  POSTED ON:{" "}
                  <span className="">{formatDate(propertyData.createdAt)}</span>
                </span>
                {propertyData.property?.builderName && (
                  <span className="font-medium">
                    BY:{" "}
                    <span className="uppercase">
                      {propertyData.property?.builderName || "OWNER"}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-b-lg shadow-2xl max-h-[70vh] overflow-y-auto">
          <form onSubmit={handleSubmit} className="px-5 py-4">
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded text-xs mb-3">
                Lead submitted successfully!
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-xs mb-3">
                {error}
              </div>
            )}

            {propertyData && (
              <div className="mb-3 bg-gray-50 rounded-lg px-3 py-2.5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                  {propertyData.title && (
                    <div>
                      <span className="text-text-secondary">Property:</span>
                      <p className="text-text-btn-brandRed font-medium line-clamp-2">
                        {propertyData.title}
                      </p>
                    </div>
                  )}
                  {propertyData.type && (
                    <div>
                      <span className="text-text-secondary">Type:</span>
                      <p className="text-text-btn-brandRed font-medium capitalize">
                        {propertyData.type}
                      </p>
                    </div>
                  )}

                  {/* UPDATED HERE */}
                  {propertyData?.location?.address?.formatted && (
                    <div>
                      <span className="text-text-secondary">Location:</span>
                      <p className="text-text-btn-brandRed font-medium">
                        {propertyData.location.address.formatted}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mb-3">
              <h3 className="text-xs font-semibold text-text-btn-brandRed mb-2 uppercase tracking-wide">
                Your Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs text-text-btn-brandRed mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="leadName"
                    value={formData.leadName}
                    onChange={handleChange}
                    required
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-btn-brandRed mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-btn-brandRed mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Email address"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-btn-brandRed mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Your location"
                  />
                </div>
              </div>
            </div>

            <div className="mb-3">
              <h3 className="text-xs font-semibold text-text-btn-brandRed mb-2 uppercase tracking-wide">
                (Optional Details)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Looking For
                  </label>
                  <input
                    type="text"
                    name="searchCriteria.searchFor"
                    value={formData.searchCriteria.searchFor}
                    onChange={handleChange}
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="e.g., 2 BHK"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Preferred Location
                  </label>
                  <input
                    type="text"
                    name="searchCriteria.propertyLocation"
                    value={formData.searchCriteria.propertyLocation}
                    onChange={handleChange}
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Preferred area"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Min Budget
                  </label>
                  <input
                    type="number"
                    name="searchCriteria.budget.min"
                    value={formData.searchCriteria.budget.min}
                    onChange={handleChange}
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Minimum"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Max Budget
                  </label>
                  <input
                    type="number"
                    name="searchCriteria.budget.max"
                    value={formData.searchCriteria.budget.max}
                    onChange={handleChange}
                    className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
                    placeholder="Maximum"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || submitted}
              className={`w-fit px-6 place-self-end py-2 mt-4 bg-btn-warning text-white text-xs font-medium rounded 
              hover:bg-opacity-90 transition-colors 
              disabled:opacity-50 disabled:cursor-not-allowed 
              flex items-center justify-center gap-2`}
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : submitted ? (
                "Submitted"
              ) : (
                "View Advertiser Details"
              )}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden fixed  md:bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-white px-4 py-2 rounded-full shadow-lg md:flex items-center gap-2">
        <MdVerifiedUser className="text-green-600" size={16} />
        <span className="text-xs text-text-secondary">
          Please fill in your details to be shared with this advertiser only.
        </span>
      </div>
    </div>,
    document.body
  );
};

export default ContactOwnerModal;

// import { useEffect, useState } from "react";
// import { createPortal } from "react-dom";
// import { IoClose } from "react-icons/io5";
// import { MdVerifiedUser } from "react-icons/md";
// import api from "../../configs/api";
// import { useAuth } from "../../context/AuthContext";
// import { Z_INDEX } from "../../utils/constants";
// import { formatDate, formatPrice } from "../../utils/helpers";
// import { showError, showSuccess } from "../../utils/toastUtils";

// const ContactOwnerModal = ({ isOpen, onClose, propertyData }) => {
//   const { user } = useAuth();
//   const [formData, setFormData] = useState({
//     leadName: "",
//     phoneNumber: "",
//     email: "",
//     location: "",
//     searchCriteria: {
//       searchFor: "",
//       propertyLocation: "",
//       budget: {
//         min: "",
//         max: "",
//       },
//     },
//     matchedPropertyId: "",
//     assignedAgent: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [submitted, setSubmitted] = useState(false);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState(false);

//   useEffect(() => {
//     if (!isOpen) return;

//     setFormData({
//       leadName: user?.fullName || "",
//       phoneNumber: user?.phone || "",
//       email: user?.email || "",
//       location: user?.preferredCity || "",
//       searchCriteria: {
//         searchFor: propertyData?.title || "",
//         propertyLocation: propertyData?.location?.address || "",
//         budget: {
//           min: "",
//           max: "",
//         },
//       },
//       matchedPropertyId: propertyData?._id || "",
//       assignedAgent: propertyData?.listedBy?._id || "",
//     });
//   }, [isOpen, user, propertyData]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     if (name.includes(".")) {
//       const keys = name.split(".");
//       setFormData((prev) => {
//         const updated = { ...prev };
//         let current = updated;

//         for (let i = 0; i < keys.length - 1; i++) {
//           current = current[keys[i]];
//         }

//         current[keys[keys.length - 1]] = value;
//         return updated;
//       });
//     } else {
//       setFormData((prev) => ({
//         ...prev,
//         [name]: value,
//       }));
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setError("");
//     setSuccess(false);

//     const submitData = {
//       leadName: formData.leadName,
//       phoneNumber: formData.phoneNumber,
//       email: formData.email,
//       location: formData.location,
//       searchCriteria: {
//         searchFor: formData.searchCriteria.searchFor,
//         propertyLocation: formData.searchCriteria.propertyLocation,
//         budget: {
//           min: formData.searchCriteria.budget.min
//             ? Number(formData.searchCriteria.budget.min)
//             : 0,
//           max: formData.searchCriteria.budget.max
//             ? Number(formData.searchCriteria.budget.max)
//             : 0,
//         },
//       },
//       matchedPropertyId: formData.matchedPropertyId,
//       assignedAgent: formData.assignedAgent,
//     };

//     try {
//       //   console.log("submitData:", submitData);
//       const response = await api.post("/leads/", submitData);

//       if (response.data.success) {
//         setSubmitted(true);
//         setSuccess(true);
//         showSuccess("Request submitted! You’ll be contacted soon.");
//         setTimeout(() => {
//           handleClose();
//           setSubmitted(false);
//         }, 1500);
//       }
//     } catch (err) {
//       setError(
//         err.response?.data?.message ||
//           "Failed to submit lead. Please try again."
//       );
//       showError("Failed to submit lead. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClose = () => {
//     setError("");
//     setSuccess(false);
//     onClose();
//     setSubmitted(false);
//   };

//   if (!isOpen) return null;

//   const getPropertyPrice = () => {
//     if (!propertyData?.property) return null;

//     if (
//       propertyData.type === "residential" ||
//       propertyData.type === "project"
//     ) {
//       return (
//         propertyData.property.floorPlan?.expectedPrice ||
//         propertyData.property.price
//       );
//     }
//     if (propertyData.type === "commercial") {
//       return propertyData.property.allowedForConstructionDetails?.expectedPrice;
//     }
//     if (propertyData.type === "land") {
//       return propertyData.property.expectedPrice;
//     }
//     return null;
//   };

//   return createPortal(
//     <div
//       style={{ zIndex: Z_INDEX.modal }}
//       className="fixed inset-0  flex items-center justify-center p-3 overflow-y-auto"
//     >
//       {/* Overlay */}
//       <div
//         className="absolute inset-0 bg-black bg-opacity-50"
//         onClick={handleClose}
//       ></div>

//       {/* Modal Container */}
//       <div className="relative z-10 w-full max-w-3xl my-auto">
//         {/* Property Info Card */}
//         {propertyData && (
//           <div className="bg-white flex flex-col gap-y-1 w-full  rounded-t-lg px-5 py-3 mb-0.5">
//             <div className="flex items-center justify-between w-full">
//               <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
//                 You are requesting to view advertiser details
//               </h3>
//               <button
//                 onClick={handleClose}
//                 className="text-text-secondary hover:text-btn-brandRed transition-colors ml-4"
//                 disabled={loading}
//               >
//                 <IoClose size={22} />
//               </button>
//             </div>
//             <div className="flex-1 items-center text-xs text-text-secondary">
//               <div className="flex items-center gap-3 ">
//                 {getPropertyPrice() && (
//                   <span className="font-semibold text-btn-brandRed">
//                     {formatPrice(getPropertyPrice())}
//                   </span>
//                 )}
//                 <span>
//                   POSTED ON:{" "}
//                   <span className="">{formatDate(propertyData.createdAt)}</span>
//                 </span>
//                 {propertyData.property?.builderName && (
//                   <span className="font-medium">
//                     BY:{" "}
//                     <span className=" uppercase">
//                       {propertyData.property?.builderName || "OWNER"}
//                     </span>
//                   </span>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Form Card */}
//         <div className="bg-white rounded-b-lg shadow-2xl max-h-[70vh] overflow-y-auto">
//           <form onSubmit={handleSubmit} className="px-5 py-4">
//             {success && (
//               <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded text-xs mb-3">
//                 Lead submitted successfully!
//               </div>
//             )}

//             {error && (
//               <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-xs mb-3">
//                 {error}
//               </div>
//             )}

//             {/* Property Description */}
//             {propertyData && (
//               <div className="mb-3 bg-gray-50 rounded-lg px-3 py-2.5">
//                 <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
//                   {propertyData.title && (
//                     <div>
//                       <span className="text-text-secondary">Property:</span>
//                       <p className="text-text-btn-brandRed font-medium line-clamp-2">
//                         {propertyData.title}
//                       </p>
//                     </div>
//                   )}
//                   {propertyData.type && (
//                     <div>
//                       <span className="text-text-secondary">Type:</span>
//                       <p className="text-text-btn-brandRed font-medium capitalize">
//                         {propertyData.type}
//                       </p>
//                     </div>
//                   )}
//                   {propertyData.location?.address && (
//                     <div>
//                       <span className="text-text-secondary">Location:</span>
//                       <p className="text-text-btn-brandRed font-medium">
//                         {propertyData.location.address}
//                       </p>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             )}

//             {/* Basic Information */}
//             <div className="mb-3">
//               <h3 className="text-xs font-semibold text-text-btn-brandRed mb-2 uppercase tracking-wide">
//                 Your Information
//               </h3>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
//                 <div>
//                   <label className="block text-xs text-text-btn-brandRed mb-1">
//                     Name
//                   </label>
//                   <input
//                     type="text"
//                     name="leadName"
//                     value={formData.leadName}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Your name"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-btn-brandRed mb-1">
//                     Phone
//                   </label>
//                   <input
//                     type="tel"
//                     name="phoneNumber"
//                     value={formData.phoneNumber}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Phone number"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-btn-brandRed mb-1">
//                     Email
//                   </label>
//                   <input
//                     type="email"
//                     name="email"
//                     value={formData.email}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Email address"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-btn-brandRed mb-1">
//                     Location
//                   </label>
//                   <input
//                     type="text"
//                     name="location"
//                     value={formData.location}
//                     onChange={handleChange}
//                     required
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Your location"
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Optional Information */}
//             <div className="mb-3">
//               <h3 className="text-xs font-semibold text-text-btn-brandRed mb-2 uppercase tracking-wide">
//                 (Optional Details)
//               </h3>
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
//                 <div>
//                   <label className="block text-xs text-text-secondary mb-1">
//                     Looking For
//                   </label>
//                   <input
//                     type="text"
//                     name="searchCriteria.searchFor"
//                     value={formData.searchCriteria.searchFor}
//                     onChange={handleChange}
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="e.g., 2 BHK"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-secondary mb-1">
//                     Preferred Location
//                   </label>
//                   <input
//                     type="text"
//                     name="searchCriteria.propertyLocation"
//                     value={formData.searchCriteria.propertyLocation}
//                     onChange={handleChange}
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Preferred area"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-secondary mb-1">
//                     Min Budget
//                   </label>
//                   <input
//                     type="number"
//                     name="searchCriteria.budget.min"
//                     value={formData.searchCriteria.budget.min}
//                     onChange={handleChange}
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Minimum"
//                     min="0"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-xs text-text-secondary mb-1">
//                     Max Budget
//                   </label>
//                   <input
//                     type="number"
//                     name="searchCriteria.budget.max"
//                     value={formData.searchCriteria.budget.max}
//                     onChange={handleChange}
//                     className="w-full px-2.5 py-1.5 text-xs border border-border rounded focus:ring-1 focus:ring-btn-brandRed focus:border-transparent outline-none"
//                     placeholder="Maximum"
//                     min="0"
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Submit Button */}
//             <button
//               type="submit"
//               disabled={loading || submitted}
//               className={`w-fit px-6 place-self-end py-2 mt-4 bg-btn-warning text-white text-xs font-medium rounded
//               hover:bg-opacity-90 transition-colors
//               disabled:opacity-50 disabled:cursor-not-allowed
//               flex items-center justify-center gap-2`}
//             >
//               {loading ? (
//                 <>
//                   <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
//                   Submitting...
//                 </>
//               ) : submitted ? (
//                 "Submitted"
//               ) : (
//                 "View Advertiser Details"
//               )}
//             </button>
//           </form>
//         </div>
//       </div>

//       {/* Bottom Secure Badge */}
//       <div className="hidden fixed  md:bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-white px-4 py-2 rounded-full shadow-lg md:flex items-center gap-2">
//         <MdVerifiedUser className="text-green-600" size={16} />
//         <span className="text-xs text-text-secondary">
//           Please fill in your details to be shared with this advertiser only.
//         </span>
//       </div>
//     </div>,
//     document.body
//   );
// };

// export default ContactOwnerModal;
